if (!customElements.get('cw-testimonial-carousel')) {
  customElements.define('cw-testimonial-carousel', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-testimonial-track]');
      this.originals = [...this.querySelectorAll('[data-slide-index]')];
      if (!this.track || !this.originals.length) return;

      this.count = this.originals.length;
      this.index = 0;
      this.position = this.count;
      this.manualPause = false;
      this.hoverPoint = null;
      this.hoveredCardIndex = null;
      this.inView = false;
      this.controls = this.querySelector('[data-testimonial-controls]');
      this.previous = this.querySelector('[data-testimonial-previous]');
      this.next = this.querySelector('[data-testimonial-next]');
      this.status = this.querySelector('[data-testimonial-status]');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      const listen = (target, event, handler, extra = {}) => target.addEventListener(event, handler, { ...options, ...extra });

      // Matching copies on both sides allow real, uniformly sized neighbor peeks.
      const copy = (slide) => {
        const clone = slide.cloneNode(true);
        clone.setAttribute('data-carousel-clone', '');
        clone.removeAttribute('data-shopify-editor-block');
        clone.removeAttribute('id');
        clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        return clone;
      };
      this.before = this.originals.map(copy);
      this.after = this.originals.map(copy);
      this.track.prepend(...this.before);
      this.track.append(...this.after);
      this.slides = [...this.before, ...this.originals, ...this.after];
      this.cards = this.slides.map((slide) => ({
        element: slide.querySelector('.cw-testimonial-card'),
        index: slide.dataset.slideIndex
      })).filter((card) => card.element);

      listen(this.track, 'pointerdown', (event) => {
        this.pointerStart = { x: event.clientX, y: event.clientY, left: this.track.scrollLeft };
        this.stopForInteraction();
      });
      listen(this.track, 'click', (event) => {
        const start = this.pointerStart;
        this.pointerStart = null;
        if (event.detail === 0 || this.count <= this.visible || (start && (
          Math.abs(event.clientX - start.x) > 8 || Math.abs(event.clientY - start.y) > 8 ||
          Math.abs(this.track.scrollLeft - start.left) > 8
        ))) return;

        // Inert neighbors retarget clicks to the track, so use their visible bounds.
        const viewport = this.track.getBoundingClientRect();
        const neighbor = this.slides.find((slide) => {
          const box = slide.getBoundingClientRect();
          return (box.left < viewport.left - 1 || box.right > viewport.right + 1) &&
            event.clientX >= Math.max(box.left, viewport.left) &&
            event.clientX <= Math.min(box.right, viewport.right) &&
            event.clientY >= box.top && event.clientY <= box.bottom;
        });
        if (!neighbor) return;
        event.preventDefault();
        this.stopForInteraction();
        this.moveBy(neighbor.getBoundingClientRect().left < viewport.left ? -1 : 1);
      });
      listen(this.track, 'touchstart', () => this.stopForInteraction(), { passive: true });
      listen(this.track, 'wheel', () => this.stopForInteraction(), { passive: true });
      listen(this.track, 'scroll', () => {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => {
          this.position = Math.round(this.track.scrollLeft / this.step);
          this.index = this.modulo(this.position);
          this.updateState();
        });
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.settle(), 120);
      });
      listen(this.track, 'scrollend', () => this.settle());
      listen(this, 'keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        this.stopForInteraction();
        if (event.key === 'Home' || event.key === 'End') {
          this.goTo(event.key === 'Home' ? 0 : this.count - 1);
        } else {
          this.moveBy(event.key === 'ArrowLeft' ? -1 : 1);
        }
      });
      listen(this.previous, 'click', () => { this.stopForInteraction(); this.moveBy(-1); });
      listen(this.next, 'click', () => { this.stopForInteraction(); this.moveBy(1); });
      const trackHover = (event) => {
        if (event.pointerType === 'touch') return;
        this.hoverPoint = event.target.closest('[data-testimonial-controls]') ? null : { x: event.clientX, y: event.clientY };
        this.updateHoveredCard();
      };
      listen(this, 'pointerover', trackHover);
      listen(this, 'pointermove', trackHover);
      listen(this, 'pointerleave', () => { this.hoverPoint = null; this.updateHoveredCard(); });
      listen(this, 'focusin', () => this.stopForInteraction());
      listen(document, 'visibilitychange', () => {
        if (document.hidden) { this.hoverPoint = null; this.updateHoveredCard(); }
        this.schedule();
      });
      listen(this.motion, 'change', () => { this.updateState(); this.schedule(); });
      listen(this, 'shopify:block:select', (event) => {
        const slide = event.target.closest('[data-slide-index]');
        if (slide && !slide.hasAttribute('data-carousel-clone')) {
          this.stopForInteraction();
          this.goTo(Number(slide.dataset.slideIndex));
        }
      });

      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this.track);
      this.intersectionObserver = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        if (!this.inView) { this.hoverPoint = null; this.updateHoveredCard(); }
        this.schedule();
      }, { threshold: 0.25 });
      this.intersectionObserver.observe(this);
      this.resize();
    }

    modulo(value) {
      return (value % this.count + this.count) % this.count;
    }

    resize() {
      const width = this.originals[0].getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.step = width + gap;
      this.visible = Math.min(this.count, parseInt(getComputedStyle(this).getPropertyValue('--cw-visible'), 10) || 1);
      this.position = this.count + this.index;
      this.track.scrollTo({ left: this.position * this.step, behavior: 'instant' });
      this.dataset.ready = 'true';
      this.updateState();
      this.schedule();
    }

    settle() {
      clearTimeout(this.settleTimer);
      if (!this.step) return;
      const position = Math.round(this.track.scrollLeft / this.step);
      this.index = this.modulo(position);
      this.position = this.count + this.index;
      if (position !== this.position) {
        // The copy is identical, so returning to the middle set is invisible.
        this.track.scrollTo({ left: this.position * this.step, behavior: 'instant' });
      }
      this.updateState();
    }

    stopForInteraction() {
      // A swipe, tap, keyboard focus, sustained card hover, or manual navigation permanently stops rotation.
      this.manualPause = true;
      clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
      this.clearTimer();
      this.updateState();
    }

    cardAtPointer() {
      if (!this.hoverPoint) return null;
      const { x, y } = this.hoverPoint;
      const viewport = this.track.getBoundingClientRect();
      if (x < viewport.left || x > viewport.right || y < viewport.top || y > viewport.bottom) return null;
      const card = this.cards.find(({ element }) => {
        const box = element.getBoundingClientRect();
        return x >= box.left && x <= box.right && y >= box.top && y <= box.bottom;
      });
      return card ? card.index : null;
    }

    updateHoveredCard() {
      const index = this.cardAtPointer();
      // Identical loop copies retain the same dwell through an invisible recenter.
      if (index === this.hoveredCardIndex) return;
      clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
      this.hoveredCardIndex = index;
      if (index === null || this.manualPause) return;
      this.hoverTimer = setTimeout(() => {
        this.hoverTimer = null;
        if (this.cardAtPointer() !== index) {
          this.updateHoveredCard();
          return;
        }
        this.stopForInteraction();
      }, 500);
    }

    moveBy(direction) {
      this.settle();
      this.scrollToPosition(this.position + direction);
    }

    goTo(index) {
      this.scrollToPosition(this.count + this.modulo(index));
    }

    scrollToPosition(position) {
      this.position = position;
      this.index = this.modulo(position);
      this.track.scrollTo({ left: position * this.step, behavior: this.motion.matches ? 'instant' : 'smooth' });
      this.updateState();
    }

    updateState() {
      const viewport = this.track.getBoundingClientRect();
      this.controls.hidden = this.count < 2;
      this.slides.forEach((slide, position) => {
        const box = slide.getBoundingClientRect();
        slide.dataset.visible = String(box.left >= viewport.left - 1 && box.right <= viewport.right + 1);
        const active = position >= this.position && position < this.position + this.visible;
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });
      this.status.setAttribute('aria-live', this.manualPause ? 'polite' : 'off');
      this.status.textContent = 'Testimonial ' + (this.index + 1) + ' of ' + this.count;
      this.dataset.currentSlide = String(this.index);
      this.dataset.autoplay = String(this.visible < 3 && this.count > this.visible && !this.manualPause && !this.motion.matches);
      this.updateHoveredCard();
    }

    clearTimer() {
      clearTimeout(this.timer);
      this.timer = null;
    }

    schedule() {
      this.clearTimer();
      if (this.visible >= 3 || this.count <= this.visible || this.manualPause || this.motion.matches ||
          !this.inView || document.hidden) return;
      this.timer = setTimeout(() => {
        this.moveBy(1);
        this.schedule();
      }, 2000);
    }

    disconnectedCallback() {
      this.clearTimer();
      clearTimeout(this.hoverTimer);
      clearTimeout(this.settleTimer);
      cancelAnimationFrame(this.scrollFrame);
      this.events?.abort();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      [...(this.before || []), ...(this.after || [])].forEach((slide) => slide.remove());
      this.originals?.forEach((slide) => {
        delete slide.dataset.visible;
        slide.removeAttribute('aria-hidden');
        slide.inert = false;
      });
      delete this.dataset.ready;
    }
  });
}
